import React, { useState, useRef } from 'react';
import {
  Chip, Box, Button, Typography, Card, CardContent, CardHeader, TextField, Grid, FormControl, InputLabel, Select, MenuItem, InputAdornment
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import LabelIcon from '@mui/icons-material/Label';
import { useTranslation } from 'react-i18next';
import TagEditor from '../Widgets/TagEditor';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useUser } from '../WebSocketContext';

const SettingWeatherAPI = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const { isReadOnly } = useUser();
  const [locationText, setLocationText] = useState('');
  const [instructionText, setInstructionText] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [mode, setMode] = useState('current');
  const [days, setDays] = useState(1);
  const [dt, setDt] = useState('');
  const [responseTagName, setResponseTagName] = useState('weather_data');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  const locationRef = useRef(null);
  const instructionRef = useRef(null);
  const apiKeyRef = useRef(null);

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType: 'weatherapi',
    onSaveStatusChange,
    customComparison: (initial) => {
      return (
        apiKey === (initial.apiKey || '') &&
        locationText === (initial.location || '') &&
        instructionText === (initial.instruction || '') &&
        mode === (initial.mode || 'current') &&
        days === (initial.days || 1) &&
        dt === (initial.dt || '') &&
        responseTagName === (initial.responseTagName || 'weather_data')
      );
    },
    customSaveData: () => ({
      apiKey,
      location: locationText,
      instruction: instructionText,
      mode,
      days,
      dt,
      responseTagName
    }),
    loadDataFunction: (data) => {
      if (data.customData?.apiKey !== undefined) setApiKey(data.customData.apiKey);
      if (data.customData?.location !== undefined) setLocationText(data.customData.location);
      if (data.customData?.instruction !== undefined) setInstructionText(data.customData.instruction);
      if (data.customData?.mode !== undefined) setMode(data.customData.mode);
      if (data.customData?.days !== undefined) setDays(data.customData.days);
      if (data.customData?.dt !== undefined) setDt(data.customData.dt);
      if (data.customData?.responseTagName !== undefined) setResponseTagName(data.customData.responseTagName);
    }
  });

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuType('');
  };

  const handleTagSelect = (value) => {
    if (menuType === 'location') {
      locationRef.current?.insertTag(value);
    } else if (menuType === 'instruction') {
      instructionRef.current?.insertTag(value);
    } else if (menuType === 'apiKey') {
      apiKeyRef.current?.insertTag(value);
    }
    handleMenuClose();
  };

  return (
    <ActionSettingsLayout
      title={t('settings.weatherapi.weatherapi_settings') || 'Weather API Configuration'}
      isEnabled={actionSettings.isEnabled}
      onEnabledChange={actionSettings.setIsEnabled}
      isSaveDisabled={actionSettings.isSaveDisabled}
      onSave={actionSettings.handleSave}
      onForceRun={actionSettings.handleForceRun}
      forceRunLoading={actionSettings.forceRunLoading}
      conditions={actionSettings.conditions}
      onConditionsChange={actionSettings.handleConditionsChange}
      popUpOpen={actionSettings.popUpOpen}
      forceRunResult={actionSettings.forceRunResult}
      onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
      forceRunWarningOpen={actionSettings.forceRunWarningOpen}
      forceRunWarningCount={actionSettings.forceRunWarningCount}
      onForceRunConfirm={actionSettings.handleForceRunConfirm}
      onForceRunCancel={actionSettings.handleForceRunCancel}
      tags={tags}
      primaryColor="#1565c0"
      tagsDropdownProps={{
        tags: tags,
        handleTagSelect: handleTagSelect,
        anchorEl: anchorEl,
        setAnchorEl: handleMenuClose,
        menuType: menuType
      }}
    >
      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #1565c010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#1565c0',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(21, 101, 192, 0.2)',
              }}>
                {t('settings.weatherapi.config', 'CONFIG')}
              </Box>
              {t('settings.weatherapi.api_configuration', 'API Configuration')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#f5f9ff',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>{t('settings.weatherapi.api_key_label', 'Weather API Key')}</Typography>
              <Box sx={{ mb: 1 }}>
                <TagEditor
                  ref={apiKeyRef}
                  value={apiKey}
                  onChange={setApiKey}
                  placeholder={t('settings.weatherapi.api_key_helper', 'Enter your Weather API key')}
                  singleLine
                  disabled={isReadOnly}
                  onTagClick={(e) => handleMenuOpen(e, 'apiKey')}
                  tags={tags}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <a href="https://www.weatherapi.com/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem', textDecoration: 'underline', color: '#1976d2' }}>
                  {t('settings.weatherapi.how_to_get_key', 'How to get the API key')}
                </a>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>{t('settings.weatherapi.location_label', 'Location (City, Zip, Lat/Long, IP)')}</Typography>
              <Box sx={{
                border: '1px solid #d0d0e8',
                borderRadius: '8px',
                mb: 1,
                backgroundColor: '#fff',
                '&:hover': {
                  borderColor: '#1565c0',
                },
                transition: 'all 0.2s ease',
              }}>
                <TagEditor
                  ref={locationRef}
                  value={locationText}
                  onChange={setLocationText}
                  placeholder={t('settings.weatherapi.location_placeholder', 'Enter location (e.g. London or {eventData})')}
                  disabled={isReadOnly}
                  singleLine
                  onTagClick={(e) => handleMenuOpen(e, 'location')}
                  tags={tags}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('settings.weatherapi.mode_label', 'Mode')}</InputLabel>
                <Select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  label={t('settings.weatherapi.mode_label', 'Mode')}
                  disabled={isReadOnly}
                >
                  <MenuItem value="current">{t('settings.weatherapi.mode_current', 'Current Weather')}</MenuItem>
                  <MenuItem value="forecast">{t('settings.weatherapi.mode_forecast', 'Forecast')}</MenuItem>
                  <MenuItem value="history">{t('settings.weatherapi.mode_history', 'History')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {mode === 'forecast' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('settings.weatherapi.days_label', 'Days (1-10)')}
                  type="number"
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value))}
                  inputProps={{ min: 1, max: 10 }}
                  disabled={isReadOnly}
                />
              </Grid>
            )}

            {mode === 'history' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('settings.weatherapi.date_label', 'Date (YYYY-MM-DD)')}
                  value={dt}
                  onChange={(e) => setDt(e.target.value)}
                  helperText={t('settings.weatherapi.date_helper', 'Supports tags, e.g. {eventTime}')}
                  disabled={isReadOnly}
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #1565c010',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box component="span" sx={{
                bgcolor: '#1565c0',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(21, 101, 192, 0.2)',
              }}>
                {t('settings.weatherapi.output', 'OUTPUT')}
              </Box>
              {t('settings.weatherapi.output_title', 'Results')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#f5f9ff',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>{t('settings.weatherapi.ai_instruction_label', 'AI Instruction (Optional)')}</Typography>
              <Box sx={{
                border: '1px solid #d0d0e8',
                borderRadius: '8px',
                mb: 1,
                backgroundColor: '#fff',
                '&:hover': {
                  borderColor: '#1565c0',
                },
                transition: 'all 0.2s ease',
              }}>
                <TagEditor
                  ref={instructionRef}
                  value={instructionText}
                  onChange={setInstructionText}
                  placeholder={t('settings.weatherapi.ai_instruction_placeholder', 'e.g. Summarize the weather in a friendly tone...')}
                  multiline
                  rows={3}
                  disabled={isReadOnly}
                  onTagClick={(e) => handleMenuOpen(e, 'instruction')}
                  tags={tags}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <Autocomplete
                  freeSolo
                  options={(tags || []).map(t => typeof t === 'string' ? t : t.variable)}
                  value={responseTagName}
                  onChange={(event, newValue) => {
                    setResponseTagName(newValue);
                  }}
                  onInputChange={(event, newInputValue) => {
                    setResponseTagName(newInputValue);
                  }}
                  disabled={isReadOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('settings.response_tag_name', 'Response Tag Name') + ' (' + t('common.required', 'Required') + ')'}
                      placeholder="weather_data"
                      required
                      fullWidth
                      size="small"
                      helperText={t('settings.response_tag_name_help', 'The result will be saved to this tag')}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </ActionSettingsLayout>
  );
};

export default React.memo(SettingWeatherAPI);
